import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiSend } from "@mdi/js";
import LoadingSpinner from "app/components/LoadingSpinner";
import CustomToast from "app/components/common/CustomToast";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import UpgradePlanModal from "app/components/WebApp/GlobalNavBar/UpgradePlanModal";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import { flashState } from "app/hooks/useFlashToast";
import AppointmentModal from "app/containers/JobCalendar/components/AppointmentModal";
import JobSetupModal from "app/containers/JobCalendar/components/JobSetupModal";
import ConfirmationModal from "app/components/common/ConfirmationModal";
import RevokeContractModal from "./RevokeContractModal";
import DeleteJobModal from "./DeleteJobModal";
import PaymentConfirmModal from "./PaymentConfirmModal";
import {
  fetchCalendarSettings,
  fetchSectionTemplates,
} from "app/containers/JobCalendar/slice";
import {
  computeContractTrack,
  computePaymentTrack,
} from "app/containers/JobCalendar/utils/sendTrackRules";
import { buildSendMessages } from "app/containers/JobCalendar/utils/sendFeedback";
import {
  fetchJobDetail,
  clearJobDetail,
  deleteJob,
  fetchDeletePreview,
  markJobPaid,
  undoManualPayment,
  cancelPaymentRequest,
  clearPayment,
  revokeContract,
  resetContractStatus,
  downloadContract,
  previewContractPdf,
} from "app/containers/Jobs/slice";
import { previewTemplatePdf } from "app/containers/ContractManagement/slice";
import { PAYMENT_CONNECTION_SECTION_ID } from "app/containers/PaymentConnection/constants";
import { useOpenStripeDashboard } from "app/containers/Jobs/hooks/useOpenStripeDashboard";
import { useJobSetupSend } from "app/containers/Jobs/hooks/useJobSetupSend";
import { toAppointmentShape } from "app/containers/Jobs/utils/toAppointmentShape";
import { formatAddress, formatMoney } from "app/containers/Jobs/utils/format";
import { openGoogleMaps } from "app/containers/JobCalendar/utils/mapUtils";
import { copyToClipboard } from "app/containers/JobCalendar/utils/emailAutomationHelpers";
import JobBookingCard from "./cards/JobBookingCard";
import JobSetupCard from "./cards/JobSetupCard";
import ContractCard from "./cards/ContractCard";
import PaymentCard from "./cards/PaymentCard";
import { paymentAmount } from "./cards/PaymentDetail";
import InspectionResultCard from "./cards/InspectionResultCard";
import ReportCard from "./cards/ReportCard";
import EmailAutomationCard from "./cards/EmailAutomationCard";
import DeleteJobCard from "./cards/DeleteJobCard";
import "./jobDetail.css";

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    device: { isMobile },
  } = useApp();

  const {
    detail,
    detailJobId,
    getJobDetailStatus,
    getJobDetailError,
    deleteJobStatus,
    deletePreview,
    deletePreviewStatus,
  } = useSelector((state) => state.jobs);
  // The backend renders booking, report and email times in this timezone;
  // payment and contract times arrive as UTC instants, so the cards format
  // them here to match.
  const accountTimeZone = useSelector(
    (state) => state.userProfile.profile?.settings?.timezone
  );
  // Tier, Connect state, templates and the Save & Send handler are shared with
  // the calendar's Job Setup modal. This page fetches its own detail.
  const {
    tier,
    stripeConnected,
    stripeLoading,
    contractTemplates,
    showUpgrade,
    openUpgrade,
    closeUpgrade,
    setupModalProps,
  } = useJobSetupSend({ jobId: id, active: true });
  const {
    onSendTracks,
    contractEmailConnected,
    contractEmailLoading,
    contractEmailWarning,
  } = setupModalProps;
  // Mirrors the backend's resolve_send_template fallback (is_default row, then
  // the system template), so the card names the contract that would actually go
  // out rather than a hardcoded guess.
  const defaultTemplate = useMemo(
    () =>
      contractTemplates.find((tpl) => tpl.is_default) ||
      contractTemplates[0] ||
      null,
    [contractTemplates]
  );
  // A system-template send returns template_name: null, so the contract card
  // needs the computed system template's real name to render a heading.
  const systemTemplateName = useMemo(
    () => contractTemplates.find((tpl) => tpl.is_system)?.name || null,
    [contractTemplates]
  );

  const defaultTemplateName = defaultTemplate?.name || null;

  const isFreeTier = tier === "Free";
  // The contract track's mailbox flags describe the account's mailbox, so the
  // Email Automation card reads the same fail-open rule from the hook.
  const mailboxState = contractEmailLoading
    ? "loading"
    : contractEmailConnected
      ? "connected"
      : "missing";

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractSending, setContractSending] = useState(false);
  // Every payment action, the payment request send included, shares this one
  // flag: no_payment offers both Mark as Paid and Send Payment Request, and
  // neither may start while the other is in flight.
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentConfirm, setPaymentConfirm] = useState(null);
  // "revoke" | "reset" | null — which contract modal is open.
  const [contractModal, setContractModal] = useState(null);
  const [contractBusy, setContractBusy] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "default",
  });

  // Memoized so the modals' init effects don't reset on unrelated re-renders.
  const appointment = useMemo(
    () => (detail ? toAppointmentShape(detail) : null),
    [detail]
  );
  const lockState = useMemo(
    () => ({
      contractStatus: detail?.contract?.status,
      paymentStatus: detail?.payment?.status,
    }),
    [detail]
  );

  // Same gating the Job Setup modal uses, so the card buttons and the Save & Send
  // checkboxes can never disagree about whether a send is possible. Without this
  // the cards let you fire a request the backend is guaranteed to reject.
  const contractTrack = useMemo(
    () =>
      computeContractTrack({
        status: detail?.contract?.status,
        tier,
        appointment,
        quote: detail?.job_setup?.quote,
        emailConnected: contractEmailConnected,
        emailLoading: contractEmailLoading,
        emailWarning: contractEmailWarning,
      }),
    [
      detail,
      tier,
      appointment,
      contractEmailConnected,
      contractEmailLoading,
      contractEmailWarning,
    ]
  );
  const paymentTrack = useMemo(
    () =>
      computePaymentTrack({
        status: detail?.payment?.status,
        tier,
        stripeConnected,
        stripeLoading,
        appointment,
        quote: detail?.job_setup?.quote,
      }),
    [detail, tier, stripeConnected, stripeLoading, appointment]
  );

  useInitialFetch(() => dispatch(fetchJobDetail(id)), { skip: !id });

  useEffect(() => {
    dispatch(fetchCalendarSettings());
    dispatch(fetchSectionTemplates());
  }, [dispatch]);

  useEffect(() => () => dispatch(clearJobDetail()), [dispatch]);

  const openStripeDashboard = useOpenStripeDashboard(setToast);

  const goBack = useCallback(() => navigate("/jobs"), [navigate]);

  // Callers await this before clearing their busy flag, so the button stays
  // disabled until the card shows the new status and can't fire twice.
  const refreshDetail = useCallback(
    () => (id ? dispatch(fetchJobDetail(id)) : Promise.resolve()),
    [dispatch, id]
  );

  // Close only. Both modals call onHide on Cancel and Esc as well as after a
  // save, so refetching here means every dismissed modal costs a full
  // GET /jobs/{id}/. The refresh hangs off onSaved instead.
  const handleCloseBooking = useCallback(() => setShowBookingModal(false), []);

  const openSetup = useCallback(() => setShowSetupModal(true), []);
  const handleCloseSetup = useCallback(() => setShowSetupModal(false), []);

  const openDeleteModal = useCallback(() => {
    setShowDeleteModal(true);
    dispatch(fetchDeletePreview(id));
  }, [dispatch, id]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await dispatch(deleteJob(id)).unwrap();
      setShowDeleteModal(false);
      navigate("/jobs", { state: flashState("Job deleted successfully") });
    } catch (message) {
      setShowDeleteModal(false);
      setToast({
        show: true,
        message: message || "Failed to delete job.",
        variant: "error",
      });
    }
  }, [dispatch, id, navigate]);

  // A non-"toggle" mode means the backend would reject the send, so route the
  // click to whatever would actually unblock the user instead of firing it.
  const handleBlockedTrack = useCallback(
    (mode) => {
      if (mode === "upgrade") {
        openUpgrade();
        return true;
      }
      if (mode === "stripe-hint") {
        setPaymentConfirm({ kind: "connect" });
        return true;
      }
      return false;
    },
    [openUpgrade]
  );

  const runSend = useCallback(
    async (kind) => {
      const isContract = kind === "contract";
      const track = isContract ? contractTrack : paymentTrack;
      if (handleBlockedTrack(track.mode)) return;

      // Tracked per card so only the card that was clicked shows as busy — a
      // shared flag greys out both and reads as if both were clicked.
      const setInFlight = isContract ? setContractSending : setPaymentBusy;
      setInFlight(true);
      const outcome = await onSendTracks({
        sendContract: isContract,
        sendPayment: !isContract,
      });

      const [{ message, variant }] = buildSendMessages({
        result: outcome,
        wantContract: isContract,
        wantPayment: !isContract,
      });
      setToast({ show: true, message, variant });
      if (outcome[kind]?.ok) await refreshDetail();
      setInFlight(false);
    },
    [
      contractTrack,
      paymentTrack,
      handleBlockedTrack,
      onSendTracks,
      refreshDetail,
    ]
  );

  const handleSendContract = useCallback(() => runSend("contract"), [runSend]);
  const handleSendPaymentRequest = useCallback(() => {
    if (handleBlockedTrack(paymentTrack.mode)) return;
    setPaymentConfirm({ kind: "send" });
  }, [handleBlockedTrack, paymentTrack]);

  // Every payment action ends in a toast plus a detail refresh.
  const runPaymentAction = useCallback(
    async (thunk, arg, successMessage) => {
      setPaymentBusy(true);
      try {
        await dispatch(thunk(arg)).unwrap();
        setToast({ show: true, message: successMessage, variant: "success" });
        await refreshDetail();
      } catch (message) {
        setToast({
          show: true,
          message:
            typeof message === "string" ? message : "Something went wrong.",
          variant: "error",
        });
      } finally {
        setPaymentBusy(false);
      }
    },
    [dispatch, refreshDetail]
  );

  const handleCopyRequestLink = useCallback(async (url) => {
    const copied = await copyToClipboard(url);
    setToast({
      show: true,
      message: copied
        ? "Payment Request Link Copied"
        : "Couldn't copy the payment request link.",
      variant: copied ? "success" : "error",
    });
  }, []);

  const handleMarkPaid = useCallback(
    () => setPaymentConfirm({ kind: "markPaid" }),
    []
  );

  const handleUndoPayment = useCallback(
    (paymentId) => setPaymentConfirm({ kind: "undo", paymentId }),
    []
  );

  const handleRestartPayment = useCallback(
    (paymentId) => setPaymentConfirm({ kind: "restart", paymentId }),
    []
  );

  const handleCancelRequest = useCallback(
    (paymentId) => setPaymentConfirm({ kind: "cancel", paymentId }),
    []
  );

  const closePaymentConfirm = useCallback(() => setPaymentConfirm(null), []);

  // The modal stays open, showing its loading state, until the action settles.
  // It is closed only if it still shows this confirmation: a send that turns
  // out to be blocked swaps in the connect prompt, which must stay up.
  const handlePaymentConfirm = useCallback(async () => {
    if (!paymentConfirm) return;
    const { kind, paymentId } = paymentConfirm;
    switch (kind) {
      case "connect":
        navigate("/user-info", {
          state: { scrollTo: PAYMENT_CONNECTION_SECTION_ID },
        });
        break;
      case "send":
        await runSend("payment");
        break;
      case "markPaid":
        await runPaymentAction(markJobPaid, { id }, "Payment marked as paid");
        break;
      case "undo":
        await runPaymentAction(
          undoManualPayment,
          paymentId,
          "Payment record cleared"
        );
        break;
      case "restart":
        await runPaymentAction(
          clearPayment,
          paymentId,
          "Payment record cleared"
        );
        break;
      case "cancel":
        await runPaymentAction(
          cancelPaymentRequest,
          paymentId,
          "Payment request cancelled"
        );
        break;
      default:
        break;
    }
    setPaymentConfirm((current) =>
      current === paymentConfirm ? null : current
    );
  }, [paymentConfirm, navigate, runSend, runPaymentAction, id]);

  const contractId = detail?.contract?.id;

  const runContractAction = useCallback(
    async (thunk, arg, successMessage) => {
      setContractBusy(true);
      try {
        await dispatch(thunk(arg)).unwrap();
        setToast({ show: true, message: successMessage, variant: "success" });
        await refreshDetail();
      } catch (message) {
        setToast({
          show: true,
          message:
            typeof message === "string" ? message : "Something went wrong.",
          variant: "error",
        });
      } finally {
        setContractBusy(false);
      }
    },
    [dispatch, refreshDetail]
  );

  const handleRevokeConfirm = useCallback(
    async ({ reasonCode }) => {
      await runContractAction(
        revokeContract,
        { contractId, reasonCode },
        "Contract revoked."
      );
      setContractModal(null);
    },
    [contractId, runContractAction]
  );

  const handleResetConfirm = useCallback(async () => {
    await runContractAction(
      resetContractStatus,
      contractId,
      "Contract status reset."
    );
    setContractModal(null);
  }, [contractId, runContractAction]);

  // No toast on success — the browser's own download is the feedback.
  const handleDownload = useCallback(async () => {
    setContractBusy(true);
    try {
      await dispatch(downloadContract(contractId)).unwrap();
    } catch (message) {
      setToast({
        show: true,
        message:
          typeof message === "string"
            ? message
            : "Couldn't download the contract.",
        variant: "error",
      });
    } finally {
      setContractBusy(false);
    }
  }, [dispatch, contractId]);

  // Opens a contract PDF in a new tab, so the inspector reads it in the
  // browser's own viewer rather than a lightbox we have to build and scroll.
  //
  // The tab is opened synchronously, before the fetch: window.open only survives
  // the popup blocker inside the click's user-gesture window, and by the time
  // the PDF bytes arrive that window has closed. `noopener` is deliberately NOT
  // passed — it makes window.open return null, and we need the handle to point
  // the tab at the blob once it resolves.
  const openPdfInNewTab = useCallback(async (fetchUrl, fallbackMessage) => {
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    setContractBusy(true);
    try {
      const url = await fetchUrl();
      // The object URL is intentionally not revoked: the new tab is still
      // reading from it, and the browser releases it when that tab unloads.
      if (tab) tab.location = url;
      else window.open(url, "_blank");
    } catch (message) {
      if (tab) tab.close();
      setToast({
        show: true,
        message: typeof message === "string" ? message : fallbackMessage,
        variant: "error",
      });
    } finally {
      setContractBusy(false);
    }
  }, []);

  // Before a send: the template that WOULD go out, so the inspector can read it
  // before sending.
  const handlePreviewTemplate = useCallback(
    () =>
      defaultTemplate &&
      openPdfInNewTab(
        () =>
          dispatch(
            previewTemplatePdf({
              templateId: defaultTemplate.id,
              isSystem: Boolean(defaultTemplate.is_system),
            })
          ).unwrap(),
        "Couldn't preview this contract template."
      ),
    [dispatch, defaultTemplate, openPdfInNewTab]
  );

  // After a send: the contract the client actually received, re-rendered from
  // the send-time snapshot. Download can't answer this while the contract is
  // out for signature — it streams BoldSign's signed PDF and 404s until one
  // exists — so an unsigned contract has no other way to be looked at.
  const handlePreviewContract = useCallback(
    () =>
      contractId &&
      openPdfInNewTab(
        () => dispatch(previewContractPdf(contractId)).unwrap(),
        "Couldn't preview this contract."
      ),
    [dispatch, contractId, openPdfInNewTab]
  );

  const handleDeferred = useCallback((label) => {
    setToast({
      show: true,
      message: `${label} will be available when that feature launches.`,
      variant: "default",
    });
  }, []);

  // The calendar and the Jobs list also load details into this slice, so a
  // detail or error left over from another job must never paint as this one.
  // A refetch of this job keeps the page up: blanking it would unmount any
  // open modal mid-action.
  const ownsDetail = String(detailJobId) === String(id);
  const hasOwnDetail = ownsDetail && String(detail?.id) === String(id);
  if (!ownsDetail || (!hasOwnDetail && getJobDetailStatus !== "failed")) {
    return <LoadingSpinner />;
  }

  if (getJobDetailStatus === "failed" || !detail) {
    return (
      <div className="job-detail-container">
        <div className="job-detail-error-state">
          <p>{getJobDetailError || "This job could not be found."}</p>
          <Button variant="outline-primary" onClick={goBack}>
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  const address = formatAddress(detail.address);

  return (
    <div className="job-detail-container">
      <button type="button" className="job-detail-back" onClick={goBack}>
        <Icon path={mdiArrowLeft} size={0.6} className="me-2" />
        Back to Jobs
      </button>

      <div className="job-detail-title-row">
        <h2 className="job-detail-address">{address}</h2>
        {address && (
          <Button
            variant="link"
            className="job-detail-nav-button"
            onClick={() => openGoogleMaps(address)}
            aria-label="Navigate to address"
          >
            <Icon path={mdiSend} size={0.75} className="job-detail-nav-icon" />
          </Button>
        )}
        <JobStatusBadge domain="inspection" value={detail.status} />
      </div>

      <div className="job-detail-cards">
        <JobBookingCard job={detail} onEdit={() => setShowBookingModal(true)} />
        <JobSetupCard job={detail} onEdit={openSetup} />

        <div className="jd-card-row">
          <ContractCard
            job={detail}
            timeZone={accountTimeZone}
            onEditTemplate={openSetup}
            defaultTemplateName={defaultTemplateName}
            systemTemplateName={systemTemplateName}
            isFreeTier={isFreeTier}
            onUpgrade={openUpgrade}
            onSendContract={handleSendContract}
            onReissue={openSetup}
            onRevoke={() => setContractModal("revoke")}
            onResetStatus={() => setContractModal("reset")}
            onDownload={handleDownload}
            onPreviewTemplate={handlePreviewTemplate}
            onPreviewContract={handlePreviewContract}
            busy={contractBusy}
            sendTrack={contractTrack}
            sending={contractSending}
          />
          <PaymentCard
            job={detail}
            timeZone={accountTimeZone}
            tier={tier}
            onEditQuote={openSetup}
            onUpgrade={openUpgrade}
            onSendPaymentRequest={handleSendPaymentRequest}
            onMarkPaid={handleMarkPaid}
            onCancelRequest={handleCancelRequest}
            onCopyRequestLink={handleCopyRequestLink}
            onOpenStripeDashboard={openStripeDashboard}
            onUndoPayment={handleUndoPayment}
            onRestartPayment={handleRestartPayment}
            sendTrack={paymentTrack}
            busy={paymentBusy}
          />
        </div>

        <div className="jd-card-row">
          <InspectionResultCard job={detail} onSetup={openSetup} />
          <ReportCard job={detail} onDeferred={handleDeferred} />
        </div>

        <EmailAutomationCard
          job={detail}
          onDeferred={handleDeferred}
          isFreeTier={isFreeTier}
          mailboxState={mailboxState}
          onUpgrade={openUpgrade}
          onSetUpEmail={() => navigate("/email-automation")}
        />
        <DeleteJobCard onDelete={openDeleteModal} />
      </div>

      <AppointmentModal
        show={showBookingModal}
        onHide={handleCloseBooking}
        onSaved={refreshDetail}
        appointment={appointment}
        initialSlot={null}
        lockState={lockState}
        onNavigateToDetail={() => setShowBookingModal(false)}
      />

      <JobSetupModal
        show={showSetupModal}
        onHide={handleCloseSetup}
        appointment={appointment}
        {...setupModalProps}
        onSaved={refreshDetail}
        onNavigateToDetail={() => setShowSetupModal(false)}
      />

      <DeleteJobModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteJobStatus === "loading"}
        preview={deletePreview}
        previewLoading={deletePreviewStatus === "loading"}
        onOpenStripe={openStripeDashboard}
        canRefundInStripe={detail?.payment?.status === "paid_stripe"}
      />

      <PaymentConfirmModal
        kind={paymentConfirm?.kind}
        amount={formatMoney(paymentAmount(detail))}
        clientName={detail.client?.name}
        onHide={closePaymentConfirm}
        onConfirm={handlePaymentConfirm}
        isLoading={paymentBusy}
      />

      <RevokeContractModal
        show={contractModal === "revoke"}
        onHide={() => setContractModal(null)}
        onConfirm={handleRevokeConfirm}
        clientName={detail.client?.name}
        isLoading={contractBusy}
      />

      <ConfirmationModal
        show={contractModal === "reset"}
        onHide={() => setContractModal(null)}
        onConfirm={handleResetConfirm}
        title="Reset Morph Contract Status"
        confirmLabel="Reset"
        isLoading={contractBusy}
        loadingLabel="Working"
        staticWhileLoading
      >
        <p className="mt-3 mb-0">
          Resetting the status will unlock the job for editing. The original
          contract won't cover your changes.
          <br />
          You should re-issue afterward to stay protected.
        </p>
      </ConfirmationModal>

      <UpgradePlanModal open={showUpgrade} onClose={closeUpgrade} />

      <CustomToast
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        message={toast.message}
        variant={toast.variant}
        isMobile={isMobile}
      />
    </div>
  );
};

// The detail is fetched once on mount, so moving between two /jobs/:id entries
// without leaving the page (browser back / forward) must remount it; otherwise
// it would wait forever on a detail that was never requested.
const JobDetailRoute = () => {
  const { id } = useParams();
  return <JobDetail key={id} />;
};

export default JobDetailRoute;
